import React from "react";
import { Col, Input, Row } from "reactstrap";

/**
 * Array:content building block (SCRUM-553, VariableField registry) — name +
 * description pairs with add/remove item buttons. Emits the full items
 * array via `onChange(name, value)` (legacy handleContentArrayUpdate
 * payload). An empty value renders one blank pair, like the legacy blocks.
 */
export type ContentItem = { name?: string; description?: string };

export type ArrayContentFieldProps = {
  settingVar: { variable: string; value?: unknown };
  onChange: (name: string, value: ContentItem[]) => void;
};

export default function ArrayContentField({ settingVar, onChange }: ArrayContentFieldProps) {
  const items: ContentItem[] =
    Array.isArray(settingVar.value) && settingVar.value.length > 0
      ? settingVar.value
      : [{ name: "", description: "" }];

  const updateItem = (idx: number, field: keyof ContentItem, newVal: string) => {
    onChange(
      settingVar.variable,
      items.map((item, i) => (i === idx ? { ...item, [field]: newVal } : item))
    );
  };

  const addItem = () =>
    onChange(settingVar.variable, [...items, { name: "", description: "" }]);

  const removeItem = (idx: number) => {
    if (items.length > 1) {
      onChange(settingVar.variable, items.filter((_, i) => i !== idx));
    }
  };

  return (
    <>
      {items.map((item, i) => (
        <div
          // index key: controlled row, no row-local state (SCRUM-2183)
          key={i}
          className="mb-3 p-2"
          style={{ border: "1px solid #e9ecef", borderRadius: "4px" }}
        >
          <Row className="mb-2">
            <Col md="2">
              <label style={{ color: "#597ade" }}>Name #{i + 1}</label>
            </Col>
            <Col md="9">
              <Input
                type="text"
                placeholder="Enter name..."
                value={item.name || ""}
                onChange={(e) => updateItem(i, "name", e.target.value)}
              />
            </Col>
            <Col md="1">
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => removeItem(i)}
                disabled={items.length === 1}
              >
                &minus;
              </button>
            </Col>
          </Row>
          <Row>
            <Col md="2">
              <label style={{ color: "#597ade" }}>Description #{i + 1}</label>
            </Col>
            <Col md="10">
              <textarea
                rows={3}
                className="form-control"
                placeholder="Enter description..."
                value={item.description || ""}
                onChange={(e) => updateItem(i, "description", e.target.value)}
              />
            </Col>
          </Row>
        </div>
      ))}
      <Row>
        <Col md={{ offset: 2, size: 10 }}>
          <button type="button" className="btn btn-primary btn-sm" onClick={addItem}>
            + Add content item
          </button>
        </Col>
      </Row>
    </>
  );
}
